
import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldX, MessageSquare, ArrowRight } from 'lucide-react';
import FadeIn from '../../../components/FadeIn';
import SEO from '../../../components/SEO';

const ObjectionHandling = () => {
    return (
        <div className="bg-white">
            <SEO
                title="Handling Sales Objections Training | BPO Consultants"
                description="Turn 'No' into 'Maybe'. Master the LAER framework to handle objections about price, timing, and competitors."
                keywords="Objection Handling, Sales Rejection, LAER Framework, Sales Training, Overcoming Objections"
            />

            {/* Hero */}
            <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-[#0B0D15] overflow-hidden">
                <div className="absolute inset-0 opacity-20">
                    <img src="https://images.unsplash.com/photo-1559526323-cb2f2fe2591b?auto=format&fit=crop&q=80&w=1200" alt="Discussion" className="w-full h-full object-cover" />
                </div>
                <div className="max-w-7xl mx-auto relative z-10 text-center">
                    <FadeIn>
                        <span className="inline-block bg-[#A2CB52]/20 text-[#A2CB52] px-4 py-2 rounded-full text-sm font-bold border border-[#A2CB52]/40 mb-6 backdrop-blur-sm">
                            <ShieldX size={14} className="inline mr-1" /> Module 3: Rebuttals
                        </span>
                        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                            Handling <br /> Objections
                        </h1>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            An objection isn't a rejection. It's a request for more information.
                        </p>
                    </FadeIn>
                </div>
            </section>

            <section className="py-20 px-4 sm:px-6 lg:px-8">
                <div className="max-w-4xl mx-auto">
                    <FadeIn className="mb-16">
                        <h2 className="text-3xl font-bold text-[#20222D] mb-6">The LAER Framework</h2>
                        <p className="text-lg text-gray-600 leading-relaxed mb-8">
                            Most reps panic when they hear "We're not interested." We teach you to remain calm and use the LAER model: Listen, Acknowledge, Explore, Respond.
                        </p>

                        <div className="grid md:grid-cols-2 gap-8">
                            {[
                                {
                                    title: "Price Objections",
                                    desc: "How to respond to 'It's too expensive' by pivoting to ROI and cost of inaction."
                                },
                                {
                                    title: "Timing Objections",
                                    desc: "'Call me next quarter' usually means 'You haven't shown value yet.' Learn to uncover the real reason."
                                },
                                {
                                    title: "Competitor Objections",
                                    desc: "how to ethically position against competitors without bad-mouthing them."
                                },
                                {
                                    title: "The 'Send Me Info' Brush-Off",
                                    desc: "Turning this common stall tactic into a firm commitment for a next step."
                                }
                            ].map((item, i) => (
                                <div key={i} className="bg-gray-50 p-6 rounded-xl border border-gray-100">
                                    <div className="text-[#A2CB52] mb-4">
                                        <MessageSquare size={28} />
                                    </div>
                                    <h3 className="text-xl font-bold text-[#20222D] mb-2">{item.title}</h3>
                                    <p className="text-gray-600 text-sm">{item.desc}</p>
                                </div>
                            ))}
                        </div>
                    </FadeIn>

                    {/* SGE / FAQ Section */}
                    <FadeIn className="mb-16">
                        <h2 className="text-2xl font-bold text-[#20222D] mb-6">Common Rebuttals</h2>
                        <div className="mt-4 space-y-4">
                            <div className="border-b border-gray-200 pb-4">
                                <h3 className="font-bold text-gray-900 mb-2">"We already have a vendor."</h3>
                                <p className="text-gray-600">Great! We're not asking you to switch today. We just want to be your backup option in case they drop the ball.</p>
                            </div>
                            <div className="border-b border-gray-200 pb-4">
                                <h3 className="font-bold text-gray-900 mb-2">"I'm busy."</h3>
                                <p className="text-gray-600">I promise to be brief. The reason for my call is...</p>
                            </div>
                        </div>
                    </FadeIn>

                    <FadeIn className="bg-[#20222D] rounded-3xl p-12 text-center relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-[#A2CB52] blur-[100px] opacity-20 pointer-events-none"></div>
                        <h2 className="text-3xl font-bold text-white mb-6">Master the Pivot</h2>
                        <Link to="/contact" className="inline-flex items-center bg-[#A2CB52] text-[#20222D] px-8 py-4 rounded-xl font-bold text-lg hover:bg-[#B5D575] transition-all transform hover:scale-105">
                            Start Module <ArrowRight className="ml-2" size={20} />
                        </Link>
                    </FadeIn>
                </div>
            </section>
        </div>
    );
};

export default ObjectionHandling;
